//import './reloader';
import {mat4, vec3, quat} from 'gl-matrix';
import {assert, DEG2RAD, resize_canvas_to_client_size, qs} from './utils';
import {create_gl, create_buffer} from './webgl';
import {use_program} from './shader-utils';
import {Camera} from './camera';
import {Orbit} from './orbit';
import {PickRay} from './pick-ray';
import {init_grid} from './grid';
import {add_hotkeys} from './hotkeys';
import {ParamList} from './params2';
import {init_sphere} from './sphere';
import {init_sculpture} from './sculpture';
import {init_camera_control} from './camera-control';
// import {init_downsample} from './downsample';
import {init_gallery} from './gallery';
import {make_fps_graph} from './fps-graph';

document.addEventListener('DOMContentLoaded', main);

function main() {
    {
        //qs('.debug').innerHTML = '2020-05-29 Fri 15:00';
    }

    const canvas = qs('.webgl-canvas');
    assert(canvas instanceof HTMLCanvasElement);

    window.gl = create_gl(canvas, {
        type: 'webgl',
        extensions: [
            'EXT_texture_filter_anisotropic',
        ],
        attribs: {
            alpha: false,
            antialias: false,
            premultipliedAlpha: false,
            preserveDrawingBuffer: false,
        },
    });

    if (0) {
        // debug info
        const info = gl.exts.WEBGL_debug_renderer_info;
        const vendor = gl.getParameter(info.UNMASKED_VENDOR_WEBGL);
        const renderer = gl.getParameter(info.UNMASKED_RENDERER_WEBGL);
        console.log('Vendor:', vendor);
        console.log('Renderer:', renderer);
        console.log('Supported extensions:');
        console.table(gl.getSupportedExtensions());
    }

    function set_camera_view(camera, opts) {
        const orbit = new Orbit;
        orbit.distance = opts.d || 1;
        orbit.rotate[1] = opts.rx || 0;
        orbit.rotate[0] = opts.ry || 0;
        orbit.update();
        orbit.get_view(camera.view);
        camera.update();
    }

    const main_camera = new Camera({
        name: 'main',
        near: 0.1,
        far: 500,
        fov: 30 * DEG2RAD,
    });
    set_camera_view(main_camera, {d: 8, rx: -0.0});

    const params = new ParamList;
    params.add_float('camera_fov', {min:10, max: 90, default: 30});

    const api_state = {
        isMuted: false,
        isWebcamEnabled: false,
        hasClickedAndHeld: false,
        isRetinaEnabled: true,
        isFpsGraphEnabled: false,
        isDofEnabled: true,
        isParamsEnabled: false,
    };

    const env = {
        canvas,
        time: 0,
        dt: 0,
        camera: main_camera,
        pickray: new PickRay(main_camera),
        light: {
            pos: vec3.fromValues(3, 3, 10),
        },
        params,
        debug: false,
        dolly: 0,

        debug_texture_camera: new Camera({
            name: 'debug_texture',
            ortho: true,
            ortho_scale: 1000,
        }),
        debug_texture: null,
        debug_texture_size: [1, 1],
        debug_texture_level: 0,
        toggle_debug_texture(opts) {
            const {tex, size} = opts;
            if (this.debug_texture === tex) {
                this.debug_texture = null;
                this.debug_texture_size = [1, 1];
            } else {
                this.debug_texture = tex;
                this.debug_texture_size = size;
            }
        },
        play_sound() {},

        api_state_change(ob) {
            let changed = false;
            for (let [k, v] of Object.entries(ob)) {
                if (api_state[k] !== v) {
                    api_state[k] = v;
                    changed = true;
                }
            }

            //Object.assign(api_state, ob);
            if (!changed)
                return;

            window.parent.postMessage({
                target: 'aysculpture',
                type: 'statechange',
                data: api_state,
            }, '*');
        },
    };

    set_camera_view(env.debug_texture_camera, {d: 1});

    // create scene objects
    const grid = init_grid(gl.exts);
    const sphere = init_sphere();
    //const downsample = init_downsample(env);
    const gallery = init_gallery(env);
    env.gallery = gallery;

    const sculpture = init_sculpture(env);

    const buf_quad = create_buffer(gl.ARRAY_BUFFER, [0,0, 1,0, 0,1, 1,1]);
    const mat = mat4.create();

    function draw_debug_texture() {
        const tex = env.debug_texture;
        if (!tex) return;

        const pgm = use_program('simpletex');
        if (!pgm) return;

        const [w, h] = env.debug_texture_size;

        mat4.identity(mat);
        mat4.scale(mat, mat, [w, h, 1]);
        mat4.translate(mat, mat, [-.5, -.5, 0]);

        // draw projection image on near plane
        pgm.uniform4f('uColor', 1, 1, 1, 1);
        pgm.uniform1i('uUseTexture', 1);
        pgm.uniformSampler2D('uTexColor', tex);
        pgm.uniformMatrix4fv('uMatViewProj', env.camera.mvp);
        gl.bindBuffer(gl.ARRAY_BUFFER, buf_quad);
        pgm.vertexAttribPointer('aPosition', 2, gl.FLOAT, false, 0, 0);
        pgm.vertexAttribPointer('aTexCoord', 2, gl.FLOAT, false, 0, 0);
        pgm.uniformMatrix4fv('uMatModel', mat);
        pgm.uniform1i('uMipLevel', env.debug_texture_level);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    function set_camera_fov_param_for_aspect_ratio(aspect) {
        params.set_value('camera_fov', aspect < 1 ? 50 : 30);
    }

    function draw() {
        const retina = api_state.isRetinaEnabled;
        if (resize_canvas_to_client_size(canvas, retina)) {
            console.log('set aspect');
            set_camera_fov_param_for_aspect_ratio(canvas.width / canvas.height);
        }

        const cw = canvas.width;
        const ch = canvas.height;
        gl.viewport(0, 0, cw, ch);
        gl.clearColor(0, 0, 0, 1);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        if (env.debug_texture) {
            env.camera = env.debug_texture_camera;
            grid.draw(env);
            gallery.draw(env);
            draw_debug_texture();
        } else {
            env.camera = main_camera;
            gallery.draw(env);
            sculpture.draw(env);
        }
    }

    let fps_graph;

    function update_fps_graph() {
        if (!api_state.isFpsGraphEnabled)
            return;

        if (!fps_graph)
            fps_graph = make_fps_graph();

        const dt = env.dt;
        if (dt) {
            fps_graph.update(dt);
            fps_graph.draw();
        }
    }

    function update(time) {
        env.dt = time - env.time;
        env.time = time;
        update_fps_graph();
        update_camera();
    }

    function update_camera() {
        // get the active camera
        const {camera, orbit} = env;

        // update the viewport to match canvas
        camera.viewport[2] = canvas.width;
        camera.viewport[3] = canvas.height;

        // update orbit & view (this should be debug only?)
        env.camera.view[14] = -8 + env.dolly;
        env.camera.fov = DEG2RAD * params.get_value('camera_fov');
        camera.update();
    }

    function animate(now) {
        requestAnimationFrame(animate);
        update(now);
        draw();
    }

    animate(0);

    // messages from host page
    window.onmessage = function(e) {
        const origin = (e.origin || e.originalEvent.origin);
        // FIXME check origin

        const msg = e.data;
        if (!(msg.target && msg.target == 'aysculpture'))
            return;

        if (msg.type == 'mute') {
            gallery.toggle_mute();
        }

        if (msg.type == 'webcam') {
            gallery.toggle_webcam();
        }

        if (msg.type == 'debug') {
            gallery.toggle_debug_texture();
        }

        if (msg.type == 'retina') {
            env.api_state_change({
                isRetinaEnabled: !api_state.isRetinaEnabled,
                isFpsGraphEnabled: true,
            });
        }

        if (msg.type == 'dof') {
            env.api_state_change({
                isDofEnabled: !api_state.isDofEnabled,
            });
            params.set_value('dof_enable', api_state.isDofEnabled)
        }

        if (msg.type == 'params') {
            env.api_state_change({
                isParamsEnabled: !api_state.isParamsEnabled,
            });
            params.toggle_visible();
            //params.set_value('dof_enable', api_state.isDofEnabled)
        }

        //window.parent.postMessage({response: 'foo'}, origin);
    };
}
